import React, { useId } from 'react';
import styled from 'styled-components';
import { CHANGE_GLYPH, Icon, Tag } from '@repo/design-system';
import { ink, semantic, space, typeRole } from '@repo/design-tokens';
import type { EntityCodeChange, FloorPlannerLayout } from '@repo/frontend-types';
import { SPACE_KIND_ICON, spaceKindWithArticle } from '@repo/ui';
import {
  asArray,
  deleteMachineSummary,
  isLive,
  kindOfLetter,
  layoutCurrentDesigns,
  letterOfCode,
  type SpaceKind,
} from '@repo/front-end-utils';

/** One row of *Renumbered at Launch*: a live space re-coded in this draft. */
export interface RenumberedRow {
  refid: string;
  name: string;
  /** The kind its new BID's letter names, or `null` for a letter no kind uses. */
  kind: SpaceKind | null;
  from: string;
  to: string;
  observers: number;
  composites: number;
}

/**
 * The rows the Launch step lists (`floor-plan-page-launch-bid-changes`, R16), from the plan alone:
 * `changes` is `pendingCodeChanges` — the record's entries still true of the plan — as the page's one
 * change source (`useDraftChanges`) reads it, so the renumbering is read once on the page. The step
 * holds no live read; an entry is only ever written for a live or unknown space, and Go Live's own
 * filter decides what it acts on. Counts are what the plan holds on the space, by the same rule the
 * space card's warning uses.
 */
export const renumberedRowsFor = (
  layout: FloorPlannerLayout | null | undefined,
  changes: readonly EntityCodeChange[],
): RenumberedRow[] =>
  changes.map(change => {
    let design: ReturnType<typeof layoutCurrentDesigns>[number] | undefined;
    let surface: { name?: string } | undefined;
    for (const d of layoutCurrentDesigns(layout)) {
      surface = asArray(d?.surfaces).find(s => isLive(s) && s.refid === change.refid);
      if (surface) {
        design = d;
        break;
      }
    }
    const summary = deleteMachineSummary(layout, design, change.refid);
    return {
      refid: change.refid,
      name: surface?.name?.trim() || change.to,
      kind: kindOfLetter(letterOfCode(change.to)),
      from: change.from,
      to: change.to,
      observers: summary.observers,
      composites: summary.composites,
    };
  });

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** *1 observer and 2 composite measures follow it.* — or *Nothing else uses it.* */
export const followsLine = ({ observers, composites }: RenumberedRow): string => {
  const parts = [
    observers > 0 ? plural(observers, 'observer', 'observers') : null,
    composites > 0 ? plural(composites, 'composite measure', 'composite measures') : null,
  ].filter(Boolean);
  return parts.length === 0 ? 'Nothing else uses it.' : `${parts.join(' and ')} follow it.`;
};

/**
 * The Launch step's *Renumbered at Launch* list (`floor-plan-page-launch-bid-changes`, R16): each live
 * space re-coded since the last launch, its old and new BID, and what moves with it. No actions — the
 * way back is *Change Kind* or Undo on the Floor Plan step. Not drawn on a frame: built from the step's
 * own block and design-system pieces as a sibling of *Removed at Launch*, under Rob's best-effort
 * ruling, for his page review. Mounted only while there is something on it.
 */
export function LaunchRenumbered({ rows }: { rows: readonly RenumberedRow[] }) {
  const headingId = useId();
  return (
    <section className="launch-block" data-renumbered="" aria-labelledby={headingId}>
      <div className="block-head">
        <Icon name="arrow-right-arrow-left" />
        <h4 id={headingId}>Renumbered at Launch</h4>
      </div>
      <p>
        These spaces get a new BID when the twin is launched. Their observers and composite measures
        move with them; their history up to now stays under the old BID.
      </p>
      <Rows>
        {rows.map(row => (
          <li key={row.refid}>
            {row.kind && <Icon name={SPACE_KIND_ICON[row.kind]} />}
            <span className="name">{row.name}</span>
            {/*
             * The lasting just-changed mark, with the renumbered glyph: a change to act on before
             * launch. The word "to", not an arrow, so the row reads aloud.
             */}
            <Tag
              surface="on-glass"
              tone={semantic.status.roles.justChanged.tagTone}
              icon={<Icon name={CHANGE_GLYPH.renumbered} size="xs" />}
            >
              {row.from} to {row.to}
            </Tag>
            <span className="counts">
              {row.kind ? `Becomes ${spaceKindWithArticle(row.kind)}. ` : ''}
              {followsLine(row)}
            </span>
          </li>
        ))}
      </Rows>
    </section>
  );
}

const Rows = styled.ul`
  list-style: none;
  margin: ${space[2]} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  li {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${space[3]};
  }

  .name {
    font-family: ${typeRole.body1Stronger.fontFamily};
    font-size: ${typeRole.body1Stronger.fontSize};
    line-height: ${typeRole.body1Stronger.lineHeight};
    font-weight: ${typeRole.body1Stronger.fontWeight};
    color: ${ink[1]};
  }

  .counts {
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    font-weight: ${typeRole.body1.fontWeight};
    color: ${ink[3]};
    flex: 1;
  }
`;
